/**
 * Configuración → «Bajas y respuestas»: el texto de baja al pie de las promociones, el botón de
 * baja, la confirmación que recibe quien se da de baja y la respuesta automática.
 *
 * `textoBaja` tiene que decir «BAJA» en mayúsculas: es la palabra que dispara la baja automática
 * de Twilio (Advanced Opt-Out). Un pie que dice «respondé baja» en minúsculas se ve igual de
 * correcto y no da de baja a nadie. El validador lo exige; acá además se muestra cómo queda.
 *
 * En Twilio real también se muestra la confirmación de Advanced Opt-Out: la alerta
 * `advanced_opt_out` del Resumen lleva a esta sección.
 */

import { useId } from "react";
import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { useCuentaOficial } from "../../contexto";
import { Tarjeta } from "../../components/Tarjeta";
import { BotoneraGuardar, useFormularioConfig } from "./SeccionEnvio";
import { TarjetaOptOut } from "./SeccionCuenta";

const MAX_TEXTO_BAJA = 60;
const MAX_BOTON_BAJA = 20;
const MAX_LARGO = 1024;

export default function SeccionBajas() {
  const { modo } = useCuentaOficial();
  const f = useFormularioConfig();
  const e = f.editado;
  if (!e) return null;

  return (
    <div className="space-y-5">
      <Tarjeta titulo="Baja en las promociones">
        <div className="space-y-5">
          <Campo label="Texto de baja al pie de las promociones" largo={e.textoBaja.length} max={MAX_TEXTO_BAJA} error={f.errores.textoBaja}
            ayuda="Es el que trae cada promoción nueva: en cada plantilla lo podés cambiar o sacar. Tiene que decir BAJA en mayúsculas.">
            {(id) => (
              <input id={id} value={e.textoBaja} onChange={(ev) => f.set("textoBaja", ev.target.value)}
                className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${f.errores.textoBaja ? "border-red-300" : "border-slate-200"}`} />
            )}
          </Campo>
          <div className="rounded-xl bg-[#e5ddd5] p-3">
            <div className="bg-white rounded-lg max-w-sm px-3 py-2 text-[13px] text-slate-800 shadow-sm">
              <p className="text-slate-400 italic">…el texto de la promoción…</p>
              <p className="mt-2">{e.textoBaja || <span className="text-slate-400">(sin pie)</span>}</p>
              <div className="border-t border-slate-100 mt-2 pt-1.5 text-center text-[12px] font-medium text-[#00a884]">{e.botonBaja || "(sin botón)"}</div>
            </div>
          </div>

          <Campo label="Botón de baja" largo={e.botonBaja.length} max={MAX_BOTON_BAJA} error={f.errores.botonBaja}
            ayuda="Cambiarlo no toca las plantillas ya aprobadas: sólo las nuevas.">
            {(id) => (
              <input id={id} value={e.botonBaja} onChange={(ev) => f.set("botonBaja", ev.target.value)}
                className={`w-full max-w-xs rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${f.errores.botonBaja ? "border-red-300" : "border-slate-200"}`} />
            )}
          </Campo>
        </div>
      </Tarjeta>

      <Tarjeta titulo="Lo que se responde">
        <div className="space-y-5">
          <Campo label="Confirmación que recibe quien se da de baja" largo={e.confirmacionBaja.length} max={MAX_LARGO} error={f.errores.confirmacionBaja}
            ayuda="{marca} se reemplaza por el nombre de la marca. Si está activada la baja automática, la confirmación la manda el proveedor de WhatsApp y esta no sale.">
            {(id) => (
              <textarea id={id} rows={3} value={e.confirmacionBaja} onChange={(ev) => f.set("confirmacionBaja", ev.target.value)}
                className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${f.errores.confirmacionBaja ? "border-red-300" : "border-slate-200"}`} />
            )}
          </Campo>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={e.respuestaAutomatica.activa}
                onChange={(ev) => f.set("respuestaAutomatica", { ...e.respuestaAutomatica, activa: ev.target.checked })}
                className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
              Respuesta automática
            </label>
            {e.respuestaAutomatica.activa && (
              <Campo label="Texto de la respuesta" largo={e.respuestaAutomatica.texto.length} max={MAX_LARGO} error={f.errores.respuestaAutomatica}>
                {(id) => (
                  <textarea id={id} rows={3} value={e.respuestaAutomatica.texto}
                    onChange={(ev) => f.set("respuestaAutomatica", { ...e.respuestaAutomatica, texto: ev.target.value })}
                    placeholder="¡Gracias por escribirnos! Te respondemos a la brevedad."
                    className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${f.errores.respuestaAutomatica ? "border-red-300" : "border-slate-200"}`} />
                )}
              </Campo>
            )}
            <p className="flex items-start gap-1.5 text-xs text-slate-500">
              <Info size={13} className="flex-shrink-0 mt-px" />
              Se manda una sola vez por persona y campaña cuando alguien responde algo que no es BAJA. Desde el 01/10/2026 Meta cobra los
              mensajes de servicio, pero los primeros 1.000 entregados de cada mes por número no pagan ese cargo (el envío sí); las respuestas automáticas usan ese
              mismo cupo.
            </p>
          </div>
        </div>
      </Tarjeta>

      <BotoneraGuardar f={f} />

      {modo === "twilio" && <TarjetaOptOut />}
    </div>
  );
}

function Campo({ label, largo, max, error, ayuda, children }: {
  label: string; largo: number; max: number; error?: string; ayuda?: string; children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label>
        <span className={`text-[11px] tabular-nums ${largo > max ? "text-red-600 font-semibold" : "text-slate-400"}`}>{largo}/{max}</span>
      </div>
      <div className="mt-1">{children(id)}</div>
      {error ? <p className="text-xs text-red-600 mt-1">{error}</p> : ayuda ? <p className="text-xs text-slate-400 mt-1">{ayuda}</p> : null}
    </div>
  );
}
